import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiChevronRight, FiX } from 'react-icons/fi';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import WorkMatchList from '../schedule/WorkMatchList';
import WorkMatchPairForm from '../schedule/WorkMatchPairForm';
import DependencyNodesTable from '../dependencies/DependencyNodesTable';

type EntityKind = 'Task' | 'Outcome';
type DetailScreen = 'reprioritize' | 'add-resources' | 'reduce-scope';
type Screen = 'root' | DetailScreen;

interface ImprovementHelperProps {
  /** Display name for the entity */
  entityName: string;
  /** The entity type (used for labeling only) */
  entityType: 'task' | 'outcome' | 'deadline';
  /**
   * The underlying task or outcome this helper operates on. Required for
   * reprioritize / add-resources / reduce-scope actions. When omitted, the
   * detail screens surface a "not enough context" message instead of failing.
   */
  target?: { kind: EntityKind; id: string };
  /**
   * Restrict the reprioritize view to a single owner's priority list. Supply
   * this when the display context is already scoped (e.g. a team dashboard
   * only prioritizes work for that team). When omitted, reprioritize shows
   * every list this item appears on.
   */
  ownerScope?: { kind: 'Team' | 'Person'; id: string };
  /** @deprecated pass `target` instead */
  entityId?: string;
}

/**
 * Sparkle badge that offers concrete actions to accelerate delivery of a task
 * or outcome: reprioritize within a team/person list, add resources (work
 * matches), or reduce scope (remove dependencies).
 */
const ImprovementHelper: React.FC<ImprovementHelperProps> = ({
  entityType,
  entityName,
  target,
  ownerScope,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [screen, setScreen] = useState<Screen>('root');
  const [lastDetail, setLastDetail] = useState<DetailScreen>('reprioritize');

  useEffect(() => {
    if (screen !== 'root') setLastDetail(screen);
  }, [screen]);

  useEffect(() => {
    if (!isOpen) setScreen('root');
  }, [isOpen]);

  const isDetail = screen !== 'root';

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-md border border-cm-border-subtle bg-cm-bg-elevated text-cm-accent-gold hover:border-cm-accent-gold hover:bg-cm-bg-hover transition-colors"
        title="How can we improve this?"
      >
        <span>&#x2728;</span>
        <span>improve</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-cm-bg-deep/70"
            onClick={() => setIsOpen(false)}
          />

          <div className="relative bg-cm-bg-surface border border-cm-border-mid rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-cm-border-subtle">
              <div className="flex items-center gap-2 min-w-0">
                {isDetail && (
                  <button
                    onClick={() => setScreen('root')}
                    className="text-cm-text-muted hover:text-cm-text-primary transition-colors -ml-1 p-1"
                    title="Back"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                  </button>
                )}
                <h3 className="font-mono font-semibold text-cm-text-primary flex items-center gap-2 truncate">
                  <span>&#x2728;</span>
                  {isDetail ? detailTitle(lastDetail) : 'How can we improve this?'}
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-cm-text-muted hover:text-cm-text-primary transition-colors"
                title="Close"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Slide viewport */}
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{ width: '200%', transform: isDetail ? 'translateX(-50%)' : 'translateX(0)' }}
              >
                <div className="w-1/2 shrink-0 p-6">
                  <RootScreen
                    entityType={entityType}
                    entityName={entityName}
                    onPick={(d) => setScreen(d)}
                  />
                </div>
                <div className="w-1/2 shrink-0 p-6 max-h-[70vh] overflow-y-auto overscroll-contain">
                  <DetailContent
                    screen={lastDetail}
                    entityName={entityName}
                    target={target}
                    ownerScope={ownerScope}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function detailTitle(screen: DetailScreen): string {
  switch (screen) {
    case 'reprioritize': return 'Reprioritize';
    case 'add-resources': return 'Add Resources';
    case 'reduce-scope': return 'Reduce Scope';
  }
}

// ---------------------------------------------------------------------------
// Root screen
// ---------------------------------------------------------------------------

const RootScreen: React.FC<{
  entityType: 'task' | 'outcome' | 'deadline';
  entityName: string;
  onPick: (screen: DetailScreen) => void;
}> = ({ entityType, entityName, onPick }) => {
  const items: { id: DetailScreen; title: string; desc: string; color: string }[] = [
    {
      id: 'reprioritize',
      title: 'Reprioritize',
      desc: `Adjust priority ordering to move this ${entityType} forward or back.`,
      color: 'text-cm-accent-blue',
    },
    {
      id: 'add-resources',
      title: 'Add Resources',
      desc: 'Assign additional people or increase allocation to accelerate delivery.',
      color: 'text-cm-accent-green',
    },
    {
      id: 'reduce-scope',
      title: 'Reduce Scope',
      desc: 'Break this into smaller pieces or defer non-critical parts.',
      color: 'text-cm-accent-purple',
    },
  ];

  return (
    <>
      <p className="text-sm text-cm-text-secondary mb-4">
        Suggestions for <span className="text-cm-accent-orange font-mono">{entityName}</span>:
      </p>
      <div className="space-y-3">
        {items.map(item => (
          <button
            key={item.id}
            onClick={() => onPick(item.id)}
            className="w-full text-left rounded-lg border border-cm-border-subtle bg-cm-bg-elevated p-3 hover:border-cm-border-mid hover:bg-cm-bg-hover transition-colors flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <h4 className={`text-xs font-mono ${item.color} mb-1`}>{item.title}</h4>
              <p className="text-xs text-cm-text-muted">{item.desc}</p>
            </div>
            <FiChevronRight className="w-4 h-4 text-cm-text-muted shrink-0" />
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-cm-text-muted text-center font-mono">
        AI-powered suggestions coming soon
      </p>
    </>
  );
};

// ---------------------------------------------------------------------------
// Detail screens
// ---------------------------------------------------------------------------

const DetailContent: React.FC<{
  screen: DetailScreen;
  entityName: string;
  target?: { kind: EntityKind; id: string };
  ownerScope?: { kind: 'Team' | 'Person'; id: string };
}> = ({ screen, entityName, target, ownerScope }) => {
  if (!target) {
    return (
      <div className="text-sm text-cm-text-muted italic py-4 text-center">
        Improvement actions aren&rsquo;t available from this context.
      </div>
    );
  }

  switch (screen) {
    case 'reprioritize':
      return <ReprioritizeScreen target={target} entityName={entityName} ownerScope={ownerScope} />;
    case 'add-resources':
      return <AddResourcesScreen target={target} />;
    case 'reduce-scope':
      return <ReduceScopeScreen target={target} />;
  }
};

// ---------------------------------------------------------------------------
// Reprioritize
// ---------------------------------------------------------------------------

interface PriorityItemView {
  id: string;
  entityType: wasm.NodeType;
  entityId: string;
  position: string;
  label: string;
  link?: string;
}

interface OwnerPriorityList {
  owner: wasm.NodeId;
  ownerLabel: string;
  items: PriorityItemView[];
  targetIndex: number;
}

const ReprioritizeScreen: React.FC<{
  target: { kind: EntityKind; id: string };
  entityName: string;
  ownerScope?: { kind: 'Team' | 'Person'; id: string };
}> = ({ target, entityName, ownerScope }) => {
  const { database, applyCommand, workspaceId } = useDatabase();
  const [tick, setTick] = useState(0); // force re-query after command

  const lists = useMemo<OwnerPriorityList[]>(() => {
    if (!database) return [];
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    tick;
    const result: OwnerPriorityList[] = [];
    const owners: wasm.NodeId[] = ownerScope
      ? [{ type_: ownerScope.kind, id: ownerScope.id }]
      : (() => {
          const raw = (database.priority_item_owners?.() ?? []) as wasm.NodeId[] | null;
          return Array.isArray(raw) ? raw : [];
        })();
    for (const owner of owners) {
      const rawItems = database.priority_items_for_owner(owner.type_, owner.id) as wasm.PriorityItem[] | null;
      if (!Array.isArray(rawItems)) continue;
      const sorted = [...rawItems].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
      const targetIndex = sorted.findIndex(i =>
        i.entity_id.type_ === target.kind && i.entity_id.id === target.id
      );
      if (targetIndex < 0) continue;

      const items: PriorityItemView[] = sorted.map(item => {
        const e = item.entity_id;
        const { label, link } = labelForNodeId(database, workspaceId, e);
        return {
          id: String(item.id),
          entityType: e.type_,
          entityId: e.id,
          position: item.position,
          label,
          link,
        };
      });

      result.push({
        owner,
        ownerLabel: labelForOwner(database, owner),
        items,
        targetIndex,
      });
    }
    return result;
  }, [database, workspaceId, target.kind, target.id, tick, ownerScope?.kind, ownerScope?.id]);

  const onPrioritizeOver = async (
    list: OwnerPriorityList,
    clickedIndex: number,
  ) => {
    const targetItemId = list.items[list.targetIndex].id;
    const afterPos = clickedIndex > 0 ? list.items[clickedIndex - 1].position : null;
    const beforePos = list.items[clickedIndex].position;
    const newPosition = positionBetween(afterPos, beforePos);

    const command: wasm.DatabaseCommand = {
      type: 'AddPriorityItem',
      owner: list.owner,
      id: targetItemId,
      entity_id: { type_: target.kind, id: target.id },
      position: newPosition,
    };
    await applyCommand(command);
    setTick(t => t + 1);
  };

  if (!database) {
    return <div className="text-sm text-cm-text-muted italic">Loading…</div>;
  }

  if (lists.length === 0) {
    const scopeLabel = ownerScope ? labelForOwner(database, { type_: ownerScope.kind, id: ownerScope.id }) : null;
    return (
      <p className="text-sm text-cm-text-muted italic">
        <span className="text-cm-accent-orange font-mono">{entityName}</span>{' '}
        {scopeLabel
          ? <>isn&rsquo;t on <span className="text-cm-text-primary">{scopeLabel}</span>&rsquo;s priority list yet.</>
          : <>isn&rsquo;t on any priority list yet.</>}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-cm-text-muted">
        Click an item to move <span className="text-cm-accent-orange font-mono">{entityName}</span> above it.
      </p>
      {lists.map(list => (
        <div key={`${list.owner.type_}:${list.owner.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-cm-bg-elevated text-cm-text-muted font-mono">
              {list.owner.type_}
            </span>
            <h4 className="text-sm font-medium text-cm-text-primary">{list.ownerLabel}</h4>
          </div>
          {list.targetIndex === 0 ? (
            <p className="text-xs text-cm-text-muted italic pl-1">Already the top priority here.</p>
          ) : (
            <div className="space-y-1">
              {list.items.slice(0, list.targetIndex).map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => onPrioritizeOver(list, i)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-cm-border-subtle bg-cm-bg-elevated hover:border-cm-accent-blue hover:bg-cm-bg-hover transition-colors text-left"
                >
                  <span className="text-xs font-mono text-cm-text-muted w-4 text-right shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-cm-bg-surface text-cm-text-muted shrink-0">
                    {item.entityType}
                  </span>
                  <span className="text-sm text-cm-text-primary truncate flex-1">{item.label}</span>
                  <span className="text-[10px] text-cm-accent-blue shrink-0">Prioritize over ↑</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add Resources
// ---------------------------------------------------------------------------

const AddResourcesScreen: React.FC<{
  target: { kind: EntityKind; id: string };
}> = ({ target }) => {
  return (
    <div className="space-y-4">
      <WorkMatchList type="dependency" dependency={{ kind: target.kind, id: target.id }} />
      <WorkMatchPairForm context={{ kind: target.kind, id: target.id }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reduce Scope
// ---------------------------------------------------------------------------

const ReduceScopeScreen: React.FC<{
  target: { kind: EntityKind; id: string };
}> = ({ target }) => {
  const { dependencyGraph, applyCommand } = useDatabase();
  const [tick, setTick] = useState(0);

  const nodeId: wasm.DependencyNodeId = target.kind === 'Task'
    ? { Task: target.id }
    : { Outcome: target.id };

  const dependencies = useMemo(() => {
    if (!dependencyGraph) return [];
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    tick;
    return dependencyGraph.get_dependencies(nodeId);
  }, [dependencyGraph, target.kind, target.id, tick]);

  const onRemove = async (depId: wasm.DependencyNodeId) => {
    const command: wasm.DatabaseCommand = {
      type: 'SetDependency',
      dependency: { by: nodeId, on: depId, annotation: null },
      enabled: false,
    };
    await applyCommand(command);
    setTick(t => t + 1);
  };

  if (!dependencyGraph) {
    return <div className="text-sm text-cm-text-muted italic">Loading…</div>;
  }

  if (dependencies.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-cm-text-muted italic mb-2">
          This {target.kind.toLowerCase()} has no dependencies to remove.
        </p>
        <p className="text-xs text-cm-text-muted">
          Reducing scope works by removing prerequisites so the remaining work can be delivered sooner.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-cm-text-muted">
        Remove a dependency to deliver sooner. Removed work is still in the database — it&rsquo;s just no longer blocking this item.
      </p>
      <DependencyNodesTable
        nodeIds={dependencies}
        emptyMessage="No dependencies"
        onRemove={onRemove}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function labelForOwner(database: wasm.WrappedDatabase, owner: wasm.NodeId): string {
  if (owner.type_ === 'Team') {
    const team = database.teams?.()?.[owner.id];
    return team ? team.name() : `Team ${owner.id.slice(0, 8)}`;
  }
  if (owner.type_ === 'Person') {
    const person = database.people()?.[owner.id];
    return person ? person.name() : `Person ${owner.id.slice(0, 8)}`;
  }
  return `${owner.type_} ${owner.id.slice(0, 8)}`;
}

function labelForNodeId(
  database: wasm.WrappedDatabase,
  workspaceId: string | undefined,
  nodeId: wasm.NodeId,
): { label: string; link?: string } {
  const base = workspaceId ? `/workspaces/${workspaceId}` : undefined;
  if (nodeId.type_ === 'Task') {
    const t = database.tasks()?.[nodeId.id];
    return {
      label: t ? t.title() : `Task ${nodeId.id.slice(0, 8)}`,
      link: base ? `${base}/tasks/${nodeId.id}` : undefined,
    };
  }
  if (nodeId.type_ === 'Outcome') {
    const o = database.outcomes()?.[nodeId.id];
    return {
      label: o ? o.title() : `Outcome ${nodeId.id.slice(0, 8)}`,
      link: base ? `${base}/outcomes/${nodeId.id}` : undefined,
    };
  }
  if (nodeId.type_ === 'WorkflowInstance') {
    const wi = database.workflow_instances()?.[nodeId.id];
    return {
      label: wi ? wi.title() || `Workflow ${nodeId.id.slice(0, 8)}` : `Workflow ${nodeId.id.slice(0, 8)}`,
      link: base ? `${base}/workflow-instances/${nodeId.id}` : undefined,
    };
  }
  return { label: `${nodeId.type_} ${nodeId.id.slice(0, 8)}` };
}

// Fractional-index midpoint: returns a string strictly between `after` and `before`
// such that after < result < before lexicographically. If `after` is null, result < before.
// Matches the alphabet the codebase already uses for positions (lowercase alpha + digits).
function positionBetween(after: string | null, before: string): string {
  const MIN_CODE = 32; // space — below digits and letters in ASCII
  const left = after ?? '';
  let i = 0;
  while (i < left.length && i < before.length && left[i] === before[i]) i++;

  const leftCode = i < left.length ? left.charCodeAt(i) : MIN_CODE;
  const beforeCode = before.charCodeAt(i);

  if (beforeCode - leftCode > 1) {
    const mid = Math.floor((leftCode + beforeCode) / 2);
    return before.slice(0, i) + String.fromCharCode(mid);
  }

  // Adjacent chars at position i. Extend `left` with 'm' — this keeps the common
  // prefix and the char that's one below `before[i]`, so result < before holds;
  // appending 'm' also ensures result > left.
  return left + 'm';
}

export default ImprovementHelper;
